import { getOwner } from '@ember/owner';
import { later } from '@ember/runloop';
import { camelize, dasherize } from '@ember/string';

import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type Model from '@ember-data/model';
import RESTSerializer from '@ember-data/serializer/rest';
import { recordIdentifierFor } from '@ember-data/store';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import type { JsonApiDocument } from '@warp-drive/core/types/spec/json-api-raw';
import { pluralize, singularize } from 'ember-inflector';

import type { DSSetup, RecordData } from 'webapp-types';
import type { ModelName } from 'webapp/enums/enums';
import {
	copyObject,
	normalizeMetricSetupData,
	serializeMetricSetupData,
} from 'webapp/utils/common';

interface RecordWithId {
	id: string | number;
}

// what RESTSerializer::normalize returns for one record
export interface NormalizedRecord {
	data: { id: string | null; type: string; attributes: object; relationships?: object };
}

// the request state service also has a private _dequeue, not in the WarpDrive types
type RequestStateService = ReturnType<Store['getRequestStateService']>;
type RequestStateServiceWithDequeue = RequestStateService & {
	_dequeue(
		identifier: ReturnType<typeof recordIdentifierFor>,
		request: ReturnType<RequestStateService['getPendingRequestsForRecord']>[number],
	): void;
};

// WarpDrive declares RESTSerializer as `any`; these are the members the app serializers use through it
interface RESTSerializerMembers {
	store: Store;
	serialize(snapshot: Snapshot, options: SerializerOptions): Record<string, unknown>;
	normalize(typeClass: ModelSchema, hash: object, prop?: string): NormalizedRecord;
	normalizeResponse(
		store: Store,
		typeClass: ModelSchema,
		payload: object,
		id: string | number,
		requestType: string,
	): JsonApiDocument;
	pushPayload(store: Store, payload: object): void;
}

// the same class, typed
const TypedRESTSerializer = RESTSerializer as new () => RESTSerializerMembers;

export default class ApplicationSerializer extends TypedRESTSerializer {
	isNewSerializerAPI = true;

	// these fields (equals the ones on the normal model) will be removed from the serialized payload
	// we don't send them to BE but we do want to use them when extracting model data (toJSON)
	getReadOnlyFields(_json: Record<string, unknown>): string[] {
		return [];
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions = {},
	): Record<string, unknown> {
		const json: Record<string, unknown> = super.serialize(snapshot, options);
		this.getReadOnlyFields(json).forEach((fieldName) => {
			delete json[fieldName];
		});
		return json;
	}

	override normalizeResponse(
		store: Store,
		typeClass: ModelSchema,
		payload: Record<string, unknown> & { deletedRecords?: RecordData.DeletedRecords },
		id: string | number,
		requestType: string,
	): JsonApiDocument {
		payload = ApplicationSerializer.checkForDeletedRecords(store, payload);
		const records = ApplicationSerializer.filterUnloadedRecords(payload);
		return super.normalizeResponse(store, typeClass, records, id, requestType);
	}

	override pushPayload(store: Store, payload: RecordData.PushPayload) {
		payload = ApplicationSerializer.checkForDeletedRecords(store, payload);
		payload = ApplicationSerializer.filterUnloadedRecords(payload);

		if (payload.account) {
			getOwner(this)!.lookup('service:account').update(payload.account);
			delete payload.account;
		}
		delete payload.origin; // BE stuff, dont need

		// make a copy bcos the payload gets mutated (serialized)
		const payloadCopy = copyObject(payload);
		return super.pushPayload(store, payloadCopy);
	}

	protected normalizeMetricSetupData(
		metricSetupData?: DSSetup.MetricSetupData | null,
	): DSSetup.MetricSetupData | null | undefined {
		return normalizeMetricSetupData(metricSetupData);
	}

	protected serializeMetricSetupData(
		metricSetupData?: DSSetup.MetricSetupData | null,
	): DSSetup.MetricSetupData | null {
		return serializeMetricSetupData(metricSetupData);
	}

	// ids of deleted records by payload key, so a late pusher update does not add them back
	static unloadedRecords: Partial<Record<RecordData.RecordKey, string[]>> = {};

	static markUnloadedRecord(type: RecordData.RecordKey, id: string) {
		if (type === 'space') {
			// don't mark spaces, as they are only permissions that can come back and are not REALLY deleted
			return;
		}
		if (type === 'sample') {
			// don't mark samples, bcos the only usecase where BE sends deletedRecords for samples
			// is when they want the loader to be shown, and will send updated sample very soon
			return;
		}
		const unloadedIds = (this.unloadedRecords[type] ??= []);
		if (!unloadedIds.includes(id)) {
			unloadedIds.push(id);
		}

		// after period of time, unmark deleted
		later(this, this.unmarkUnloadedRecord, type, id, 30000);
	}

	static unmarkUnloadedRecord(type: RecordData.RecordKey, id: string) {
		const unloadedIds = this.unloadedRecords[type];
		if (unloadedIds) {
			this.unloadedRecords[type] = unloadedIds.filter((i) => i !== id);
		}
	}

	// returns the payload without the unloaded records (single records of a marked type move into its list)
	static filterUnloadedRecords(payload: object): Record<string, unknown> {
		const unloadedRecords = this.unloadedRecords,
			records = payload as Record<string, unknown>;

		if (!Object.keys(unloadedRecords).length) {
			return records;
		}

		const filteredPayload: Record<string, unknown> = {};
		Object.keys(records).forEach((key) => {
			const pluralKey = pluralize(key),
				isArray = key === pluralKey,
				unloadedIds = unloadedRecords[singularize(key) as RecordData.RecordKey];
			if (unloadedIds) {
				const list = (filteredPayload[pluralKey] ??= []) as RecordWithId[];
				if (isArray) {
					(records[key] as RecordWithId[]).forEach((item) => {
						if (!unloadedIds.includes(String(item.id))) {
							list.push(item);
						}
					});
				} else {
					const record = records[key] as RecordWithId | null;
					if (record && !unloadedIds.includes(String(record.id))) {
						list.push(record);
					}
				}
			} else {
				filteredPayload[key] = records[key];
			}
		});

		return filteredPayload;
	}

	static checkForDeletedRecords<P extends { deletedRecords?: RecordData.DeletedRecords }>(
		store: Store,
		payload: P,
	): P {
		if (payload?.deletedRecords) {
			this.handleDeletedRecords(store, payload.deletedRecords);
			delete payload.deletedRecords;
		}
		return payload;
	}

	static handleDeletedRecords(store: Store, deletedRecords: RecordData.DeletedRecords) {
		(Object.keys(deletedRecords) as RecordData.ListKey[]).forEach((listKey) => {
			const recordIds = deletedRecords[listKey] || [],
				recordKey = singularize(listKey) as RecordData.RecordKey;

			recordIds.forEach((recordId) => {
				const record = store.peekRecord(dasherize(recordKey), recordId) as Model | null;
				// our own delete still in flight unloads the record when it finishes (BufferedModel.delete);
				// unloading it here first breaks that request's commit
				if (record && record.currentState.stateName !== 'root.deleted.inFlight') {
					this.removeInFlightState(record);

					const state = record.currentState.stateName;
					if (state && !state.includes('deleted')) {
						record.deleteRecord();
					}

					// separate runloop bcos of https://github.com/emberjs/data/issues/5395
					later(() => {
						this.markUnloadedRecord(recordKey, String(recordId));
						this.unloadLocalRecord(record);
					}, 5);
				}
			});
		});
	}

	static removeInFlightState(record: Model) {
		if (record.currentState.isSaving) {
			const identifier = recordIdentifierFor(record),
				stateService =
					record.store.getRequestStateService() as RequestStateServiceWithDequeue;
			if (stateService) {
				// this is a relatively wonky solution so the record unload does not throw an error
				// bcos only records not in inFlight state can be unloaded
				// this will likely cause a non-breaking error ("Expected Cache to have a resource entry...") after the request actually finishes
				stateService.getPendingRequestsForRecord(identifier).forEach((request) => {
					stateService._dequeue(identifier, request);
				});
			}
		}
	}

	static markRecordAsIgnored(record: Model) {
		// mark ID as deleted so we don't update it over pusher
		// is unmarked again after x seconds - check unmarkUnloadedRecord
		const modelType = (record.constructor as typeof Model).modelName as ModelName,
			modelId = record.id;

		if (modelType && modelId) {
			// the camelized model name is its payload key
			this.markUnloadedRecord(camelize(modelType) as RecordData.RecordKey, modelId);
		}
	}

	static unloadLocalRecord(record: Model) {
		this.removeInFlightState(record);
		try {
			record.unloadRecord();
		} catch (_e) {
			// skip
		}
	}

	static undeleteLocalRecord(record: Model) {
		try {
			record.rollbackAttributes();
		} catch (_e) {
			// already unloaded, nothing to undelete
		}
	}
}
